import {ArrowDownTrayIcon, ArrowPathIcon, DocumentMagnifyingGlassIcon, EnvelopeIcon, ExclamationTriangleIcon} from "@heroicons/react/16/solid";
import {isUserProcessActive, isUserProcessNoRecords, UserProcess} from "@ots-frontend/ots-data";
import {OtsActionButton, OtsActionLink, OtsAsyncActionButton} from "../components/OtsButton";
import {OtsProgressBar} from "../components/OtsProgressBar";
import {formatFileSize, formatProcessDuration, formatProcessProgress, processKind} from "./process-ui";
import {ProcessKindIcon} from "./ProcessKindIcon";
import {ProcessStatusBadge} from "./ProcessStatusBadge";
import {ProcessTags} from "./ProcessTags";

export type ProcessRowAction = "cancel" | "retry";

export type ProcessRowProps = {
  process: UserProcess;
  onCancel?: (process: UserProcess) => void;
  onRetry?: (process: UserProcess) => void;
  /**
   * Opens what the process saved about itself: its report warnings, or for a failure the findings or message
   * behind it. Offered only when there's something saved to show.
   */
  onShowDetails?: (process: UserProcess) => void;
  /** The action in flight for this process, shown as a spinner on its button. */
  busyAction?: ProcessRowAction;
  className?: string;
};

const canRetry = (status: string) => status === "FAILED" || status === "PARTIAL" || status === "CANCELED";

/** One process in the tray: what it is, how far along, and what the person can do with it. */
export const ProcessRow = ({
  process,
  onCancel,
  onRetry,
  onShowDetails,
  busyAction,
  className,
}: ProcessRowProps) => {
  const active = isUserProcessActive(process);
  const failed = process.status === "FAILED";
  const unseen = !active && !process.seen;
  const progress = formatProcessProgress(process);
  const duration = active ? undefined : formatProcessDuration(process.startDttm, process.endDttm);
  const warnings = !failed && process.warningCount > 0 ? process.warningCount : 0;
  const failureDetails = failed && (process.hasDiagnostics || process.failureMessage != null);

  return <article
    aria-label={process.name}
    data-status={process.status}
    data-kind={processKind(process)}
    data-unseen={unseen || undefined}
    className={["relative flex flex-col gap-1.5 py-3 pl-4 pr-3", className].filter(Boolean).join(" ")}>
    {unseen && <span
      aria-label="New"
      className="absolute left-1 top-[1.15rem] size-1.5 rounded-full bg-[var(--ots-primary)]"/>}

    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <ProcessKindIcon kind={processKind(process)}/>
        <h3 className="min-w-0 text-sm font-semibold text-[var(--ots-text)]">{process.name}</h3>
      </div>
      <ProcessStatusBadge
        status={process.status}
        cancelRequested={process.cancelRequested}
        noRecords={isUserProcessNoRecords(process)}
        className="shrink-0"/>
    </div>

    {process.description && <p className="text-xs text-[var(--ots-text-muted)]">{process.description}</p>}
    <ProcessTags tags={process.tags}/>

    {active && <div className="flex flex-col gap-1">
      {process.status === "QUEUED"
        ? <p className="text-xs text-[var(--ots-text-muted)]">Waiting to start</p>
        : <>
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="min-w-0 truncate text-[var(--ots-text)]">{process.step ?? "Starting"}</span>
            {progress && <span className="shrink-0 tabular-nums text-[var(--ots-text-muted)]">{progress}</span>}
          </div>
          <OtsProgressBar
            size="compact"
            label={`${process.name} progress`}
            value={progress ? process.progressDone ?? 0 : undefined}
            max={progress ? process.progressTotal : undefined}
            valueText={progress}/>
        </>}
      {process.notifyEmail && <p className="flex items-center gap-1 text-xs text-[var(--ots-text-muted)]">
        <EnvelopeIcon aria-hidden="true" className="size-3.5"/>
        We'll email you when it's done.
      </p>}
    </div>}

    {!active && (process.summary || duration) && <p
      className={["text-xs", failed ? "text-[var(--ots-error)]" : "text-[var(--ots-text-muted)]"].join(" ")}>
      {[process.summary, duration && `took ${duration}`].filter(Boolean).join(" · ")}
    </p>}

    {warnings > 0 && <p className="flex items-center gap-1 text-xs text-[var(--ots-on-warning-container)]">
      <ExclamationTriangleIcon aria-hidden="true" className="size-3.5 text-[var(--ots-warning)]"/>
      {warnings === 1 ? "1 warning" : `${warnings.toLocaleString()} warnings`}
      {process.hasDiagnostics && onShowDetails && <button
        type="button"
        onClick={() => onShowDetails(process)}
        className="ml-1 font-semibold text-[var(--ots-primary)] underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]">
        Review
      </button>}
    </p>}

    {(process.files.length > 0 || (active && onCancel) || (canRetry(process.status) && onRetry) || (failureDetails && onShowDetails)) &&
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
        {process.files.map((file) => file.downloadUrl && <OtsActionLink
          key={file.id}
          href={file.downloadUrl}
          download={file.fileName}
          target="_blank"
          rel="noopener noreferrer"
          size="mini"
          variant="tonal"
          icon={<ArrowDownTrayIcon/>}
          title={[file.fileName, formatFileSize(file.contentLength)].filter(Boolean).join(" · ")}>
          Download
        </OtsActionLink>)}
        {/* active processes have no files, so Cancel leads the row; -ml keeps its text in line with the column */}
        {active && onCancel && <OtsAsyncActionButton
          size="mini"
          variant="text"
          isLoading={busyAction === "cancel"}
          progressLabel="Canceling"
          className="-ml-2"
          disabled={process.cancelRequested}
          onClick={() => onCancel(process)}>
          Cancel
        </OtsAsyncActionButton>}
        {failureDetails && onShowDetails && <OtsActionButton
          size="mini"
          variant="secondary"
          icon={<DocumentMagnifyingGlassIcon/>}
          onClick={() => onShowDetails(process)}>
          See details
        </OtsActionButton>}
        {canRetry(process.status) && onRetry && <OtsAsyncActionButton
          size="mini"
          variant="secondary"
          icon={<ArrowPathIcon/>}
          isLoading={busyAction === "retry"}
          progressLabel="Retrying"
          onClick={() => onRetry(process)}>
          Retry
        </OtsAsyncActionButton>}
      </div>}
  </article>;
};
